import { Form, Formik, FormikErrors } from "formik";
import { useState } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";

import { useTenant } from "src/components/LoggedInAdminProvider";
import FormikErrorBanner from "src/components/form/FormikErrorBanner";
import FormikSwitchField from "src/components/form/FormikSwitchField";
import { listAllAdmins_listAdmins } from "src/graphql/generated/listAllAdmins";
import { tenantOwnerDeleteVariables } from "src/graphql/generated/tenantOwnerDelete";
import { getErrorMessage } from "src/util/errors";

type FormValues = Omit<tenantOwnerDeleteVariables, "adminId">;
type FormValuesAndErrors = FormValues & {
  saveErrors?: any;
};

export default function RemoveOwnerDialog({
  onClose,
  onSubmit,
  admin
}: {
  onClose: () => void;
  onSubmit: (values: tenantOwnerDeleteVariables) => Promise<void>;
  admin: null | listAllAdmins_listAdmins;
}): JSX.Element {
  const [saveErrors, setSaveErrors] = useState<string | null>(null);

  const handleSubmit = async (payload: FormValues) => {
    setSaveErrors(null);

    try {
      await onSubmit({ ...payload, adminId: admin!.id });
    } catch (e) {
      setSaveErrors(getErrorMessage(e));
    }
  };

  return (
    <Dialog open={!!admin}>
      <DialogTitle>Remove Organization Owner</DialogTitle>
      {admin && (
        <Formik
          initialValues={{
            deleteAllMemberships: true
          }}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, values }) => (
            <RemoveOwnerForm
              onClose={onClose}
              isSubmitting={isSubmitting}
              saveErrors={saveErrors}
              admin={admin}
              values={values}
            />
          )}
        </Formik>
      )}
    </Dialog>
  );
}

function RemoveOwnerForm({
  isSubmitting,
  onClose,
  saveErrors,
  admin,
  values
}: {
  isSubmitting?: boolean;
  onClose: () => void;
  saveErrors: string | null;
  admin: listAllAdmins_listAdmins;
  values: FormValues;
}) {
  const tenant = useTenant()!;

  return (
    <Form>
      <DialogContent>
        <p>
          You are removing <strong>{admin.fullName}</strong> ({admin.email}) as
          an Organization Owner.
        </p>

        <FormikErrorBanner
          renderBanner={(errors: FormikErrors<FormValuesAndErrors>) => {
            if (errors.saveErrors) {
              return `Error removing Organization Owner: ${errors.saveErrors}`;
            }
            return "Please fix the errors indicated below.";
          }}
          additionalErrors={saveErrors}
        />
        <FormikSwitchField
          name="deleteAllMemberships"
          label={
            tenant.isUsingSubaccounts
              ? "Also deactivate their account on all subaccounts"
              : "Also deactivate their account"
          }
        />
        {!values.deleteAllMemberships && (
          <Alert severity="warning">
            If you choose not to deactivate their account, this user will retain
            the role of “Owner”
            {tenant.isUsingSubaccounts
              ? " on all subaccounts"
              : " in this dialer organization"}
            .
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button disabled={isSubmitting} onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
          color="primary"
          variant="contained"
        >
          {isSubmitting ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Form>
  );
}
